import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import { DEFAULT_LOGO_URL } from "../constants";
import { useSignOut } from "../../hooks/useSignOut";
import LogoutIcon from "@mui/icons-material/Logout";
import { OutlinedButton } from "../styled/outlined-button";
import {
  getDocumentationForm,
  getPatientDetails,
} from "../../api/users-patients";
import { DocumentationPage } from "./documentation-page";
import { IDocument, IUserData } from "../../interface/IDocument";
import { TabButtonPatient } from "../styled/button-tab-patient";
import { ChipSoftInf } from "../styled/chip-tap";
import { UserProgramContext } from "../../context/user-program/user-program";

export const DetailPatient = () => {
  const { role } = useContext(UserProgramContext);
  const [detailPatient, setDetailPatient] = useState<IUserData | null>(null);
  const [documentation, setDocumentation] = useState<IDocument[]>([]);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { signOut } = useSignOut();
  const { patient } = useParams();
  const id = patient?.toString();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryParam = queryParams.get("filter") || "";
  const dataFetchedRef = useRef(false);

  const fetchDocumentationForm = useCallback(
    async (category: string | undefined) => {
      try {
        const response = await getDocumentationForm(id, category);
        setDocumentation(response.data);
      } catch (error) {
        console.error("Error fetching documentation:", error);
      }
    },
    [id]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (role === "clinic-weight" || role === "clinic-dashboard") {
          navigate(`/patients/${id}?filter=dateien`);
        }
        setLoading(true);
        if (!dataFetchedRef.current) {
          const patientData = await getPatientDetails(id);
          setDetailPatient(patientData);

          dataFetchedRef.current = true;
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          navigate("/PageNotFound");
        }
        console.error("Error fetching patient details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchDocumentationForm(categoryParam);
  }, [categoryParam, id, fetchDocumentationForm, navigate, role]);

  return (
    <Fragment>
      <Grid
        container
        flexDirection="column"
        alignItems="center"
        pt={2}
        pr={2}
        pl={2}
        pb={5}
      >
        {/* Header */}
        <Grid container xs={12} justifyContent="space-between" item>
          <Link to="/">
            <Button size="small">
              <img
                src={DEFAULT_LOGO_URL}
                alt="Clinic Logo"
                style={{ height: "3em" }}
              />
            </Button>
          </Link>
          <OutlinedButton
            color="inherit"
            size={isMobile ? "small" : "medium"}
            startIcon={<LogoutIcon />}
            onClick={signOut}
          >
            Abmelden
          </OutlinedButton>
        </Grid>

        {/* Card */}
        {loading ? (
          <CircularProgress
            sx={{ marginBottom: "114.5px", marginTop: "114px" }}
          />
        ) : (
          <Grid
            container
            mt={4}
            mb={6}
            width="100%"
            spacing={2}
            flexDirection="row"
            alignItems="center"
          >
            {role === "clinic-weight" || role === "clinic-dashboard" ? (
              <>
                <Grid item xs={12} mb={2}>
                  <Typography variant="h4" component="h4">
                    {detailPatient?.userAcuityScheduling?.greeting}{" "}
                    {detailPatient?.userAcuityScheduling?.firstName}{" "}
                    {detailPatient?.userAcuityScheduling?.lastName}
                  </Typography>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6} p={1}>
                  <Grid item xs={12} display="flex">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        Telefon:
                      </Typography>
                    </Box>
                    <Box width={"70%"} ml={{ xs: 0, sm: -5 }}>
                      {detailPatient?.userAcuityScheduling?.phone ? (
                        <Typography variant="body2" fontWeight={600}>
                          {detailPatient?.userAcuityScheduling?.phone}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} display="flex">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        E-Mail:
                      </Typography>
                    </Box>
                    <Box width={"75%"} ml={{ xs: 0, sm: -5 }}>
                      {detailPatient?.userAcuityScheduling?.email ? (
                        <Typography variant="body2" fontWeight={600}>
                          {detailPatient?.userAcuityScheduling?.email}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} display="flex">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        Geburtsdatum:
                      </Typography>
                    </Box>
                    <Box width={"75%"} ml={{ xs: 0, sm: -5 }}>
                      {detailPatient?.userAcuityScheduling?.birthdayDate ? (
                        <Typography variant="body2" fontWeight={600}>
                          {detailPatient?.userAcuityScheduling?.birthdayDate}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm={6} p={1}>
                  <Grid item xs={12} display="flex" alignItems="center">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        Patient seit:
                      </Typography>
                    </Box>
                    <Box width={"75%"}>
                      {detailPatient?.userAcuityScheduling?.dateCreated ? (
                        <Typography variant="body2" fontWeight={600} mb={2}>
                          {detailPatient?.userAcuityScheduling?.dateCreated}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} display="flex" alignItems="center">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        Adipositaszentrum:
                      </Typography>
                    </Box>
                    <Box width={"75%"}>
                      <Typography variant="body2" fontWeight={600} mb={2}>
                        Weight Doctors
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} display="flex" alignItems="center">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2">Anschrift:</Typography>
                    </Box>
                    <Box width={"75%"}>
                      {detailPatient?.userAcuityScheduling?.address ? (
                        <Typography variant="body2" fontWeight={600}>
                          {detailPatient?.userAcuityScheduling?.address}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} mb={2}>
                  <Typography variant="h4" component="h4">
                    {detailPatient?.userAcuityScheduling?.greeting}{" "}
                    {detailPatient?.userAcuityScheduling?.firstName}{" "}
                    {detailPatient?.userAcuityScheduling?.lastName}
                  </Typography>
                  <Divider />
                </Grid>

                {/* Patient Details */}

                <Grid item xs={12} sm={4} p={1}>
                  <Grid item xs={12} display="flex">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        Telefon:
                      </Typography>
                    </Box>
                    <Box width={"70%"} ml={{ xs: 0, sm: -5 }}>
                      {detailPatient?.userAcuityScheduling?.phone ? (
                        <Typography variant="body2" fontWeight={600}>
                          {detailPatient?.userAcuityScheduling?.phone}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} display="flex">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        E-Mail:
                      </Typography>
                    </Box>
                    <Box width={"75%"} ml={{ xs: 0, sm: -5 }}>
                      {detailPatient?.userAcuityScheduling?.email ? (
                        <Typography variant="body2" fontWeight={600}>
                          {detailPatient?.userAcuityScheduling?.email}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} display="flex">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        Geburtsdatum:
                      </Typography>
                    </Box>
                    <Box width={"75%"} ml={{ xs: 0, sm: -5 }}>
                      {detailPatient?.userAcuityScheduling?.birthdayDate ? (
                        detailPatient?.userAcuityScheduling.birthdayDate
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={3.5}
                  p={1}
                  mr={{ xs: 0, sm: 3 }}
                  ml={{ xs: 0, sm: -5 }}
                >
                  <Grid item xs={12} display="flex" alignItems="center">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        Patient seit:
                      </Typography>
                    </Box>
                    <Box width={"75%"}>
                      {detailPatient?.userAcuityScheduling?.dateCreated ? (
                        <Typography variant="body2" fontWeight={600} mb={2}>
                          {detailPatient?.userAcuityScheduling?.dateCreated}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} display="flex" alignItems="center">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        Adipositaszentrum:
                      </Typography>
                    </Box>
                    <Box width={"75%"}>
                      {detailPatient?.userTypeform?.clinicName ? (
                        <Typography variant="body2" fontWeight={600} mb={2}>
                          {detailPatient?.userTypeform?.clinicName}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} display="flex" alignItems="center">
                    <Box width={"55%"}>
                      <Typography variant="subtitle2" mb={2}>
                        Verlaufslänge:
                      </Typography>
                    </Box>
                    <Box width={"75%"}>
                      {detailPatient?.userTypeform?.courseDuration ? (
                        <Typography variant="body2" fontWeight={600} mb={2}>
                          {detailPatient?.userTypeform?.courseDuration}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm={4.5} p={1}>
                  {role !== "clinic-weight" && role !== "clinic-dashboard" && (
                    <Grid item xs={12} display="flex" alignItems="center">
                      <Box width={"55%"} mb={2}>
                        <Typography variant="subtitle2" mb={2}>
                          Erforderliche Befunde:
                        </Typography>
                      </Box>
                      <Box width={"75%"} mb={2}>
                        {detailPatient?.userTypeform?.documentationType ? (
                          <Typography variant="body2" fontWeight={600} mb={2}>
                            {detailPatient?.userTypeform?.documentationType
                              .map((value) => value.toString())
                              .join(", ")}
                          </Typography>
                        ) : (
                          <ChipSoftInf />
                        )}
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} display="flex" alignItems="center">
                    <Box width={"55%"} mb={3}>
                      <Typography variant="subtitle2">Anschrift:</Typography>
                    </Box>
                    <Box width={"75%"} mb={2}>
                      {detailPatient?.userAcuityScheduling?.address ? (
                        <Typography variant="body2" fontWeight={600}>
                          {detailPatient?.userAcuityScheduling?.address}
                        </Typography>
                      ) : (
                        <ChipSoftInf />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        )}

        {/* Buttons  */}

        <Grid container item spacing={1}>
          {role !== "clinic-weight" && role !== "clinic-dashboard" && (
            <>
              <Grid item>
                <TabButtonPatient
                  category={categoryParam}
                  value=""
                  onClick={() => navigate(`/patients/${id}`)}
                >
                  dokumentation
                </TabButtonPatient>
              </Grid>
              <Grid item>
                <TabButtonPatient
                  category={categoryParam}
                  value="testergebnisse"
                  onClick={() => {
                    navigate(`/patients/${id}?filter=${"testergebnisse"}`);
                  }}
                >
                  Testergebnisse
                </TabButtonPatient>
              </Grid>
              <Grid item>
                <TabButtonPatient
                  category={categoryParam}
                  value="ernährungstherapie"
                  onClick={() => {
                    navigate(`/patients/${id}?filter=${"ernährungstherapie"}`);
                  }}
                >
                  ernährungstherapie
                </TabButtonPatient>
              </Grid>
              <Grid item>
                <TabButtonPatient
                  category={categoryParam}
                  value="terminhistorie"
                  onClick={() => {
                    navigate(`/patients/${id}?filter=${"terminhistorie"}`);
                  }}
                >
                  terminhistorie
                </TabButtonPatient>
              </Grid>
              <Grid item>
                <TabButtonPatient
                  category={categoryParam}
                  value="dateien"
                  onClick={() => {
                    navigate(`/patients/${id}?filter=${"dateien"}`);
                  }}
                >
                  Dateien
                </TabButtonPatient>
              </Grid>
            </>
          )}
        </Grid>

        <DocumentationPage
          patientId={id}
          detailPatient={detailPatient}
          documentation={documentation}
          documentationUpdate={setDocumentation}
        />
      </Grid>
    </Fragment>
  );
};
